<template>
  <product-main
    :product="BpmOsgpoModule.PRODUCT"
    :loaded="loaded"
    :loader-text="loaderText"
  >
    <!-- Wizard -->
    <section
      v-if="!isPaymentSuccessVisible"
      class="bpm-ogpo-page d-flex flex-column h-100"
    >
      <bpm-rewrite-linked-alert
        :linked="rewriteLinkedPolicies"
        :contract-global="rewriteContractNumber"
      />

      <wizard-stepper
        class="flex-shrink-0"
        :step="wizardStep"
        :steps="wizardSteps"
        :disabled="isFormDisabled"
        :step-reachable="isWizardStepReachable"
        @update:step="onWizardStepChange"
      />

      <div class="flex-grow-1 min-h-0 d-flex flex-column overflow-hidden">
        <bpm-ogpo-participants-step
          v-if="wizardStep === 0"
          :policy="policy"
          :mode="productMode"
          :module="BpmOsgpoModule"
          :change-disabled="isFormDisabled"
          :active-bp-processes-by-type="activeBusinessProcessesByType"
          @switch-insurer="openSwitchInsuredModal"
          @open-client-modal="openClientModal"
          @delete-insurer="deleteInsurer"
          @change-process-status="changeProcessStatus"
          @open-bpm-modal="openBusinessProcessModal"
          @edit-rejected-client="editRejectedClient"
          @delete-process="deleteProcess"
          @add-insurer="openInsurerAddModal"
          @delete-insured="deleteInsured"
          @add-insured="openInsuredAddModal"
          @set-changed-vehicle="setChangedVehicle"
          @delete-vehicle="deleteVehicle"
          @edit-rejected-vehicle="editRejectedVehicle"
          @update:is-drive="setPolicyIsDrive"
          @retry-client-verify="verifyClient"
          @retry-vehicle-verify="verifyVehicle"
        />

        <bpm-ogpo-treaty-step
          v-else-if="wizardStep === 1"
          :policy="policy"
          :mode="productMode"
          :module="BpmOsgpoModule"
          :change-disabled="isFormDisabled"
          :dictionaries="dictionaries"
          :duration="treatyDuration"
          :treaty-min-date="treatyMinDate"
          :treaty-min-end-date="treatyMinEndDate"
          :treaty-max-end-date="treatyMaxEndDate"
          @add-dgpo="addEmptyDgpo"
          @update:dgpo="policy.dgpo = $event"
          @update:treaty-date="policy.treaty.date = $event"
          @update:treaty-term="policy.treaty.term = $event"
          @update:duration="treatyDuration = $event"
          @update:manager="policy.manager = $event"
        />

        <bpm-ogpo-summary-step
          v-else-if="wizardStep === 2"
          :policy="policy"
          :mode="productMode"
          :module="BpmOsgpoModule"
          :change-disabled="isFormDisabled"
          :dictionaries="dictionaries"
          :duration="treatyDuration"
          :has-calculated-premium="hasCalculatedPremium"
          @reset-calculation="resetPremiumCalculation"
        />
      </div>

      <wizard-footer
        :hint-message="wizardFooterHint.message"
        :show-back="wizardFooterShowBack"
        :back-disabled="isFormDisabled"
        :primary-action="wizardFooterPrimaryAction"
        @back="goToPreviousWizardStep"
        @primary="onWizardFooterPrimary"
      />
    </section>

    <!-- Checkout result -->
    <bpm-ogpo-payment-success
      v-if="isPaymentSuccessVisible"
      :policy-number="policy.number"
    />

    <!-- Global overlay -->
    <bpm-ogpo-global-loader
      v-if="isGlobalLoaderVisible"
      :message="checkoutStatusMessage"
    />

    <!-- Modals -->
    <vehicle-bpm-modal
      :key="vehicleModalState.hash"
      :car-added-list="vehicleModalExcludedCars"
      :vehicle="vehicleModalState"
      :mode="productMode"
      :module="BpmOsgpoModule"
      @search-vehicle="searchVehicle"
    />
    <customer-bpm-modal
      :key="clientModalState.hash"
      :client-added-list="clientModalExcludedClients"
      :client="clientModalState"
      :mode="productMode"
      :module="BpmOsgpoModule"
      @search-client="searchClient"
    />
    <process-bpm-modal
      :process="modalBusinessProcess"
      :closable="isBusinessProcessModalClosable"
      :mode="productMode"
      :module="BpmOsgpoModule"
      @update-process="updateBusinessProcess"
      @close-modal="closeBusinessProcessModal"
    />
    <customer-bpm-switch-modal
      :insureds="switchModalInsureds"
      :mode="productMode"
      :module="BpmOsgpoModule"
      @switch-insurer="switchInsurer"
    />
  </product-main>
</template>

<script setup>
import { defineProps } from 'vue'

import ProductMain from '@/components/Main/ProductMain.vue'
import WizardStepper from '@/components/Wizard/WizardStepper'
import WizardFooter from '@/components/Wizard/WizardFooter'

import BpmOgpoParticipantsStep from '@/components/Form/Bpm/Wizard/BpmOgpoParticipantsStep'
import BpmOgpoTreatyStep from '@/components/Form/Bpm/Wizard/BpmOgpoTreatyStep'
import BpmOgpoSummaryStep from '@/components/Form/Bpm/Wizard/BpmOgpoSummaryStep'
import BpmRewriteLinkedAlert from '@/components/Form/Bpm/Wizard/BpmRewriteLinkedAlert'
import BpmOgpoPaymentSuccess from '@/components/Form/Bpm/Wizard/BpmOgpoPaymentSuccess'
import BpmOgpoGlobalLoader from '@/components/Form/Bpm/Wizard/BpmOgpoGlobalLoader'

import CustomerBpmModal from '@/components/Form/Bpm/Customer/CustomerBpmModal'
import CustomerBpmSwitchModal from '@/components/Form/Bpm/Customer/CustomerBpmSwitchModal'
import VehicleBpmModal from '@/components/Form/Bpm/Vehicle/VehicleBpmModal'
import ProcessBpmModal from '@/components/Form/Bpm/Process/ProcessBpmModal'

import { useBpmOgpo } from '@/composables/bpm/useBpmOgpo'

defineProps({
  draftData: Object,
})

const {
  // Module & policy
  BpmOsgpoModule,
  policy,
  productMode,

  // Page UI state
  loaded,
  loaderText,
  isFormDisabled,
  isGlobalLoaderVisible,
  isPaymentSuccessVisible,

  // Treaty step
  dictionaries,
  treatyDuration,
  treatyMinDate,
  treatyMinEndDate,
  treatyMaxEndDate,
  hasCalculatedPremium,

  // Rewrite banner
  rewriteLinkedPolicies,
  rewriteContractNumber,

  // Wizard
  wizardStep,
  wizardSteps,
  wizardFooterHint,
  wizardFooterShowBack,
  wizardFooterPrimaryAction,
  isWizardStepReachable,
  goToPreviousWizardStep,
  onWizardStepChange,
  onWizardFooterPrimary,
  resetPremiumCalculation,

  // Participants step
  activeBusinessProcessesByType,
  openInsurerAddModal,
  openInsuredAddModal,
  setPolicyIsDrive,
  addEmptyDgpo,
  openSwitchInsuredModal,
  openClientModal,
  deleteInsurer,
  deleteInsured,
  setChangedVehicle,
  deleteVehicle,
  editRejectedClient,
  editRejectedVehicle,
  switchInsurer,
  verifyClient,
  verifyVehicle,

  // Business processes
  changeProcessStatus,
  openBusinessProcessModal,
  deleteProcess,
  updateBusinessProcess,
  closeBusinessProcessModal,

  // Modals state
  clientModalState,
  vehicleModalState,
  clientModalExcludedClients,
  vehicleModalExcludedCars,
  modalBusinessProcess,
  isBusinessProcessModalClosable,
  switchModalInsureds,
  checkoutStatusMessage,

  // Modal actions
  searchClient,
  searchVehicle,
} = useBpmOgpo()
</script>
